import * as t from 'io-ts';

import {makeEnumRuntimeType, RCalendarEventUidRef, RCalendarEventVersionUidRef} from './apiTypes';
import {parseParticipant, RParticipant, serializeParticipant} from './participantModel';
import {VisualParticipantModel} from './visualParticipantModel';

/*
 * Runtime type.
 */

export enum CalendarEventStatusesEnum {
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  TENTATIVE = 'tentative',
}

export enum CalendarEventVisualStatusesEnum {
  COMPLETE = 'COMPLETE',
  DRAFT = 'DRAFT',
}

// Common properties between draft and complete calendar events.
const RBaseCalendarEvent = t.readonly(
  t.type({
    uid: t.string,
    status: makeEnumRuntimeType(CalendarEventStatusesEnum),
    participants: t.readonlyArray(RParticipant),
    recurrence: t.union([t.readonlyArray(t.string), t.undefined]),
    recurringCalendarEventId: t.union([t.Integer, t.undefined]),
    originalStartDate: t.union([t.Integer, t.undefined]),
    calendarEventVersionUids: t.readonlyArray(RCalendarEventVersionUidRef),
    attendeesCanInviteOthers: t.boolean,
    attendeesCanModify: t.boolean,
    shouldIgnoreAttendeeResponse: t.boolean,
    updatedAt: t.Integer,
  }),
);

const RDraftCalendarEvent = t.intersection([
  RBaseCalendarEvent,
  t.type({
    visualStatus: t.literal(CalendarEventVisualStatusesEnum.DRAFT),
  }),
]);

const RCompleteCalendarEvent = t.intersection([
  RBaseCalendarEvent,
  t.type({
    visualStatus: t.literal(CalendarEventVisualStatusesEnum.COMPLETE),
    id: t.Integer,
  }),
]);

const RCalendarEventDirty = t.readonly(
  t.taggedUnion('visualStatus', [RCompleteCalendarEvent, RDraftCalendarEvent]),
  'calendarEvent',
);

/*
 * Type.
 */

export interface DraftCalendarEventModel extends t.TypeOf<typeof RDraftCalendarEvent> {}
export interface CompleteCalendarEventModel extends t.TypeOf<typeof RCompleteCalendarEvent> {}

export type CalendarEventModel = DraftCalendarEventModel | CompleteCalendarEventModel;

export const RCalendarEvent = t.clean<CalendarEventModel>(RCalendarEventDirty as any);

RCalendarEventUidRef.setTargetType(RCalendarEvent);

/*
 * IO methods.
 */

export function parseCalendarEvent(src: any): CalendarEventModel {
  return {
    id: src.id,
    uid: src.uid,
    visualStatus: CalendarEventVisualStatusesEnum.COMPLETE,
    status: src.status,
    participants: src.participants.map(parseParticipant),
    recurrence: src.recurrence || undefined,
    recurringCalendarEventId: src.recurring_calendar_event_id || undefined,
    originalStartDate: src.original_start_date_time || undefined,
    calendarEventVersionUids: src.calendar_event_version_uid ? [src.calendar_event_version_uid] : [],
    attendeesCanInviteOthers: Boolean(src.attendees_can_invite_others),
    attendeesCanModify: Boolean(src.attendees_can_modify),
    shouldIgnoreAttendeeResponse: Boolean(src.ignore_attendee_response),
    updatedAt: src.updated_at,
  };
}

export function serializeCalendarEvent(
  calendarEvent: CalendarEventModel,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  return {
    uid: calendarEvent.uid,
    participants: visualParticipants.map(serializeParticipant),
    status: calendarEvent.status,
    recurrence: calendarEvent.recurrence,
    original_start_date_time: calendarEvent.originalStartDate,
    recurring_calendar_event_id: calendarEvent.recurringCalendarEventId,
  };
}
